<template>
  <div>
    <DsEyebrow
      v-if="showMetadata"
      :text="typeLabel"
    />
    <h1 class="ds-heading-large">
      {{ content.title }}
    </h1>
    <p class="ds-margin-top-12 ds-margin-bottom-24">
      {{ content.partnerDescription }}
    </p>

    <DsImageElement
      v-if="content.introMedia"
      :image="content.introMedia"
      :screen="screen"
      class="ds-margin-bottom-48"
    />

    <!--      Metadata in mobile/table mode-->
    <template v-if="showMetadata && content.partnerMetadata">
      <PartnersMetadataAccordion
        :metadata="content.partnerMetadata"
        class="ds-margin-bottom-48"
      />
    </template>

    <!--      About block-->
    <div
      v-if="content.aboutTitle"
      class="ds-margin-bottom-48"
    >
      <h3 class="ds-heading-small">
        {{ content.aboutTitle }}
      </h3>
      <RichText
        :data="content.aboutDescription"
        class="ds-margin-top-12"
      />
    </div>

    <!--      Quote-->
    <DsQuoteSimple
      v-if="content.quote"
      :contained="false"
      :logo="content.quote.logo"
      :text="content.quote.quote"
      :author="content.quote.author"
      :extra-info="content.quote.role"
      class="ds-margin-bottom-48"
    />

    <!--      Logo bar-->
    <DsLogoBar
      v-if="content.logoBar"
      :items="content.logoBar.items"
      align-center
      class="ds-margin-bottom-48"
    />

    <!--      Services block-->
    <div
      v-if="content.contentBlockServices"
      class="ds-margin-bottom-48"
    >
      <h3 class="ds-heading">
        {{ content.contentBlockServices.title }}
      </h3>
      <p
        class="ds-margin-top-12 ds-margin-bottom-48"
        v-text="content.contentBlockServices.description"
      />
      <div
        v-for="(service, index) in content.contentBlockServices.items"
        :key="service.id"
      >
        <h4
          class="ds-heading-xsmall"
          v-text="service.title"
        />
        <RichText
          :data="service.description"
          class="ds-margin-top-12"
        />
        <div
          v-if="index < content.contentBlockServices.items.length - 1"
          class="ds-margin-y-24"
        >
          <DsDivider />
        </div>
      </div>
    </div>

    <!--      Media video-->
    <DsVideoPlayer
      v-if="content.mediaVideo"
      v-bind="content.mediaVideo"
      :aria-label-play="labels.play"
      :aria-label-close="labels.close"
      :screen="screen"
      class="ds-margin-bottom-48"
    />

    <!--      Case study-->
    <DsQuote
      v-if="content.caseStudy"
      :image="content.caseStudy.image"
      :text="content.caseStudy.quote"
      :author="content.caseStudy.author"
      :extra-info="content.caseStudy.role"
      image-position="right"
      :screen="quoteScreenSize"
      contained
      :tag="content.caseStudy.tag"
      class="ds-margin-bottom-48"
    />

    <!--      Steps block-->
    <div
      v-if="content.contentBlockSteps"
      class="ds-margin-bottom-48"
    >
      <h3 class="ds-heading ds-margin-bottom-48">
        {{ content.contentBlockSteps.title }}
      </h3>
      <!--      <p class="ds-margin-top-12 ds-margin-bottom-24" v-text="content.contentBlockSteps.description" />-->
      <div
        v-for="(step, index) in content.contentBlockSteps.items"
        :key="step.id"
      >
        <div class="ds-display-flex ds-align-items-center">
          <div class="step-icon ds-margin-right-24">
            {{ index + 1 }}
          </div>
          <div>
            <h4
              class="ds-heading-xsmall"
              v-text="step.title"
            />
            <RichText :data="step.description" />
          </div>
        </div>

        <div
          v-if="index < content.contentBlockSteps.items.length - 1"
          class="ds-margin-y-24"
        >
          <DsDivider />
        </div>
      </div>
    </div>

    <!--      Image + text-->
    <DsMediaTextBlock
      v-if="content.contentBlock"
      :title="content.contentBlock.title"
      :image="content.contentBlock.image"
      :screen="screen"
      orientation="top"
      full-width-content
      full-width-media
      class="ds-margin-bottom-48"
    >
      <RichText :data="content.contentBlock.description" />
    </DsMediaTextBlock>

    <!--      Docs block-->
    <div
      v-if="content.documentation"
      class="ds-margin-bottom-48"
    >
      <h3 class="ds-heading ds-margin-bottom-48">
        {{ content.documentation.title }}
      </h3>
      <div
        v-for="(doc, index) in content.documentation.items"
        :key="doc.id"
      >
        <DsAnchorElement
          :nuxt="doc.link.nuxt"
          :url="doc.link.url"
        >
          <div class="ds-display-flex ds-align-items-center ds-justify-content-space-between">
            <div>
              <h4
                class="ds-heading-xsmall"
                v-text="doc.title"
              />
              <RichText :data="doc.description" />
            </div>
            <DsIcon
              category="arrows"
              name="external-link"
            />
          </div>
        </DsAnchorElement>

        <div
          v-if="index < content.documentation.items.length - 1"
          class="ds-margin-y-24"
        >
          <DsDivider />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  DsAnchorElement,
  DsDivider,
  DsIcon,
  DsImageElement,
  DsLogoBar,
  DsMediaTextBlock,
  DsQuote,
  DsQuoteSimple,
  DsVideoPlayer,
  DsEyebrow
} from 'awds'
import PartnersMetadataAccordion from '../partners-metadata-accordion/partners-metadata-accordion.vue'

const { isScreenSmall, screen } = useScreen()
const { labels } = useGlobalData()

const props = defineProps({
  content: {
    type: Object,
    default: () => ({})
  },
  showMetadata: {
    type: Boolean,
    default: false
  }
})

const typeLabel = computed(() => {
  return labels.value[`${props.content.partnerType?.toLowerCase() || 'tech'}Partner`]
})
const quoteScreenSize = computed(() => {
  // We need to hack the screen size, because the content is always in a limited width like a tablet
  return isScreenSmall.value ? 'small' : 'medium'
})
</script>

<style lang="scss" scoped>
@import 'awds/foundation-vars.scss';

.step-icon {
  align-items: center;
  background-color: $color-grey-3200;
  border-radius: 50%;
  color: $color-white;
  display: flex;
  flex-shrink: 0;
  height: 40px;
  justify-content: center;
  width: 40px;
}
</style>
